import { type Metadata } from 'next';
import {
  any,
  assoc,
  assocPath,
  equals,
  omit,
  path,
  pick,
  pipe,
  split,
  values,
} from 'ramda';
import {
  type ResponsiveImageType,
  type SeoOrFaviconTag,
  toNextMetadata,
} from 'react-datocms';

import {
  type FullParams,
  type PageType,
  getCanonicalUrlArgs,
  getPageUrl,
  internalToRouteMapping,
  isPageIndexable,
  splitUrlRegion,
} from '@/app/urls/url-mapping';
import { regionToMainCountry } from '@/lib/utils';
import { type Language } from '@/lib/utils/language';
import { type Region, type RegionCountry, type UrlRegion } from '@/types';

export type SeoMetadata = {
  _seoMetaTags: SeoOrFaviconTag[];
};

export type Alternates = Partial<
  Record<`${Language}-${RegionCountry}` | 'x-default', string>
>;

export const getAlternateLocale = (
  region: Region,
  language: Language
): Exclude<keyof Alternates, 'x-default'> =>
  `${language}-${regionToMainCountry(region)}`;

/**
 * Adds Google's `x-default` hreflang entry.
 *
 * Always points at the US/EN equivalent of the current page when that
 * alternate exists; otherwise falls back to the US homepage.
 *
 * @example
 * withDefaultHreflang({
 *   'es-es': 'https://www.ironhack.com/es/desarrollo-web',
 *   'en-us': 'https://www.ironhack.com/us/web-development',
 * })
 * // → also includes 'x-default': 'https://www.ironhack.com/us/web-development'
 */
export const withDefaultHreflang = (languages: Alternates): Alternates => ({
  ...languages,
  'x-default':
    languages['en-us'] ??
    getPageUrl('home', { language: 'en', region: 'us' }, { withOrigin: true }),
});

const getCanonicalUrl = async (
  pageType: PageType,
  params: FullParams
): Promise<string> => {
  // Occasionally the canonical URL is changed for SEO purposes
  const [canonType, canonParams] = await getCanonicalUrlArgs(pageType, params);
  const canonical =
    getPageUrl(canonType, canonParams, { withOrigin: true }) ?? '';

  const experimentIndexInUrl = canonical.indexOf('/e/');
  return experimentIndexInUrl === -1
    ? canonical
    : canonical.slice(0, experimentIndexInUrl);
};

/**
 * Converts Dato `_seoMetaTags` into Next.js Metadata (title, description,
 * openGraph, twitter, etc.), including CMS global SEO fallbacks.
 */
export const seoTagsToMetadata = (
  seoMetaTags: SeoOrFaviconTag[] | null | undefined
): Metadata => {
  const metadata = seoMetaTags?.length ? toNextMetadata(seoMetaTags) : {};
  return assocPath(['openGraph', 'type'], 'website', metadata);
};

/**
 * Dato's intelligent SEO fallback can use editor-only page fields as the
 * document title. Remove those values so Next.js inherits the layout title.
 */
export const removeDatoGeneratedTitle = (
  metadata: Metadata,
  stringFields: Record<string, string | undefined>,
  fallback: Metadata
): Metadata => {
  const generatedTitles = values(stringFields);
  const isGeneratedTitle = (title: unknown): boolean =>
    typeof title === 'string' &&
    any(equals<string | undefined>(title), generatedTitles);

  const titlePaths = ['title', 'openGraph.title', 'twitter.title'];
  const updatedMetadata = titlePaths
    .map(split('.'))
    .filter((titlePath) => pipe(path(titlePath), isGeneratedTitle)(metadata))
    .reduce<Metadata>(
      (acc, titlePath) => assocPath(titlePath, path(titlePath, fallback), acc),
      metadata
    );

  return updatedMetadata;
};

type OpenGraphImageInput = Pick<
  ResponsiveImageType,
  'alt' | 'height' | 'src' | 'width'
>;

type OpenGraphImageOutput = Omit<OpenGraphImageInput, 'src'> & {
  url: OpenGraphImageInput['src'];
};

/**
 * Maps a Dato responsive image to the Open Graph image object Next.js expects
 * (`url` instead of `src`).
 */
export const toOpenGraphImage = (
  image: OpenGraphImageInput | null | undefined
): OpenGraphImageOutput | null => {
  if (!image) return null;

  const { src } = image;
  const ogImage = pipe<
    [OpenGraphImageInput],
    Omit<OpenGraphImageInput, 'src'>,
    OpenGraphImageOutput
  >(
    pick(['alt', 'height', 'width']),
    assoc('url', src)
  )(image);

  return ogImage;
};

/**
 * Sets `openGraph.images` and `twitter.image` from a Dato responsive image.
 * Leaves metadata unchanged when the image is missing.
 */
export const withOpenGraphImage = (
  metadata: Metadata,
  image: OpenGraphImageInput | null | undefined
): Metadata => {
  const ogImage = toOpenGraphImage(image);
  if (!ogImage) return metadata;

  const ogMetadata = pipe<[Metadata], Metadata, Metadata>(
    assocPath(['openGraph', 'images'], ogImage),
    assocPath(['twitter', 'image'], ogImage.url)
  )(metadata);

  return ogMetadata;
};

/**
 * Merges a canonical URL into Metadata as `openGraph.url` without replacing
 * other Open Graph fields from `toNextMetadata`.
 */
export const withOpenGraphUrl = (
  metadata: Metadata,
  url: string | null | undefined
): Metadata =>
  url ? assocPath(['openGraph', 'url'], url, metadata) : metadata;

// Removes params that we don't want when creating alternate region URLs:
// - pagination because that page may not exist in the other region
// - slug because the slug might be different in the other region
export const sanitizeAlternateParams = omit(['pagination', 'slug']);

export const getAlternateMetaTags = async (
  pageType: PageType,
  params: FullParams
): Promise<Pick<Metadata, 'alternates'>> => {
  if (!isPageIndexable(pageType, params)) return {};

  const pageRoutes = internalToRouteMapping[pageType];
  const canonical = await getCanonicalUrl(pageType, params);
  const nonCanonical = getPageUrl(pageType, params, { withOrigin: true });

  // Don't include alternate language links if the canonical URL is changed
  if (!pageRoutes || canonical !== nonCanonical)
    return { alternates: { canonical } };

  const paramsWithoutPagination = sanitizeAlternateParams(params);
  const pageRegions = Object.keys(pageRoutes) as UrlRegion[];

  const languages = pageRegions.reduce<Alternates>((regionAcc, urlRegion) => {
    const { region, language } = splitUrlRegion(urlRegion);

    const url = getPageUrl(
      pageType,
      { ...paramsWithoutPagination, language, region },
      { withOrigin: true }
    );

    if (
      !url ||
      !isPageIndexable(pageType, { language, region }) ||
      region === 'ww'
    )
      return regionAcc;

    const locale = getAlternateLocale(region, language);
    return { ...regionAcc, [locale]: url };
  }, {});

  return {
    alternates: { canonical, languages: withDefaultHreflang(languages) },
  };
};

export const getRobotsMetaTags = (
  pageType: PageType,
  params: FullParams
): Pick<Metadata, 'robots'> =>
  isPageIndexable(pageType, params)
    ? {}
    : {
        robots: {
          index: false,
          ...(params.region === 'ww' && { follow: true }),
        },
      };

/**
 * Merges CMS SEO metadata with robots + alternates, and sets `openGraph.url`
 * from the canonical without clobbering other Open Graph fields.
 */
export const composePageMetadata = async (
  pageType: PageType,
  params: FullParams,
  seo: Metadata
): Promise<Metadata> => {
  const alternates = await getAlternateMetaTags(pageType, params);
  const canonical =
    typeof alternates.alternates?.canonical === 'string'
      ? alternates.alternates.canonical
      : await getCanonicalUrl(pageType, params);

  const meta = {
    ...withOpenGraphUrl(seo, canonical),
    ...getRobotsMetaTags(pageType, params),
    ...alternates,
  };
  return meta;
};
